/**
 * Yup schemas for the auth forms — the rules of HexagieApp
 * `src/shared/utils/validations.ts`, expressed as one object per form.
 *
 * Two deliberate differences from the app, both so the schema can be a
 * module constant instead of a value rebuilt on every render:
 *
 *  - `channelType` and `country` live IN the values object rather than being
 *    closed over as `getSignUpSchema(isPhone, countryCode)` arguments. A Yup
 *    schema is not cheap to construct, and the app's shape forces a rebuild
 *    on every keystroke unless the caller remembers to memoize it.
 *  - The phone test reads its country from `this.parent`, so it can never go
 *    stale against the selected country.
 *
 * `google-libphonenumber` is the app's validator, not `libphonenumber-js`,
 * and that choice is load-bearing: `phoneUtil.parse` ignores the region
 * argument when the value already carries a `+` country code, so a user who
 * types the full international number is accepted rather than being told it
 * is invalid.
 */

import { PhoneNumberUtil } from "google-libphonenumber";
import * as Yup from "yup";

import { MIN_SIGNUP_AGE, latestDobForAge } from "./age";

const phoneUtil = PhoneNumberUtil.getInstance();

export const validatePhoneNumber = (value, countryCode) => {
  if (!value) return false;

  try {
    const number = phoneUtil.parse(String(value), countryCode);
    return phoneUtil.isValidNumber(number);
  } catch {
    return false;
  }
};

/**
 * Known live rule, kept for parity. It rejects hyphens, apostrophes and
 * non-ASCII letters, so O'Brien, Nana-Ama and Müller all fail. Loosening it
 * on web alone would let the two clients accept different names, so it
 * changes in both or neither.
 */
export const FULL_NAME_PATTERN = /^[a-zA-Z]{2,}(?:\s[a-zA-Z]{2,})+$/;

/** One field, two rule sets, chosen by the sibling `channelType`. */
const contactField = (fieldName) =>
  Yup.string()
    .trim()
    .when("channelType", {
      is: "email",
      then: (schema) =>
        schema
          .required("Email is required")
          .email("Invalid email address"),
      otherwise: (schema) =>
        schema
          .required("Phone number is required")
          .test(
            `${fieldName}-is-valid-phone`,
            "Invalid phone number for selected country",
            function isValid(value) {
              return validatePhoneNumber(value, this.parent.country);
            }
          ),
    });

/** Get Started — one contact, phone or email. */
export const CONTACT_SCHEMA = Yup.object().shape({
  channelType: Yup.string().required(),
  country: Yup.string().required(),
  input: contactField("input"),
});

/** Tell us about you — the second contact plus the profile draft. */
export const SIGNUP_SCHEMA = Yup.object().shape({
  channelType: Yup.string().required(),
  country: Yup.string().required(),
  contactInput: contactField("contactInput"),
  fullName: Yup.string()
    .trim()
    .required("Full name is required")
    .matches(FULL_NAME_PATTERN, "Please enter first and last name"),
  dob: Yup.string()
    .required("Date of birth is required")
    .test(
      "min-age",
      `You must be at least ${MIN_SIGNUP_AGE} years old`,
      (value) => !value || value <= latestDobForAge()
    ),
  gender: Yup.string().required("Gender is required"),
  usage: Yup.string().required("Usage is required"),
  address: Yup.string().trim().required("Address is required"),
});
